// src/screens/HomePage/index.js
//
// Misti's main screen.
//
// FOR NOW THE AI ASSISTANT IS OFF (AI_ASSISTANT below): her wake word gets a
// fixed reply (WAKE_REPLY) instead of a conversation, and the Dograh widget is
// not even loaded. Everything the assistant needs is still in this file, as
// described below - set AI_ASSISTANT to true to bring it back.
//
// The voice loop is Dograh end to end: it captures the mic, runs STT (Sarvam
// saaras:v3 with language auto-detect), the LLM, and TTS.
//
// The Dograh call connects at power-on and stays up. The wake word does not
// start and stop the call - it opens and closes the MICROPHONE. That way the
// WebRTC connection is already warm when a guest speaks, so there is no setup
// pause, while the agent still hears nothing until it is called for.
//
//   boot       -> call connects, mic closed
//   wake word  -> mic opens
//   Misti asks something    -> mic stays open, the guest just answers
//   Misti states something  -> mic closes, wake word needed again
//
// The wake word is not needed for every turn. Whether it is depends on what
// Misti just said: if she asked a question she is obviously waiting for an
// answer, so making someone say "Alexa" to reply to "what would you like to
// drink?" would be absurd. If she only gave information, the exchange is over.
//
// All of this is driven by rtf-bot-stopped-speaking and rtf-bot-text, tapped
// off the widget's WebSocket (see services/dograhEventTap). MIC_OPEN_MS is only
// a backstop for a turn that never gets answered at all.
//
// Dograh caps a call at 20 minutes, so the session will drop on its own; an
// effect below reconnects it.
//
// What this file also owns:
//   - feeding the face: every conversational event above is also dispatched
//     to Face/engine, which turns it into state + emotion (see Face/bella)
//   - teleoperation: the mobile app can push text for Misti to speak
//   - subtitles, and the one-tap start button the browser's mic permission needs
//   - Aeonix branding: the engraved logo, and the "made by" card while she rests (Brand.js)

import React, { useState, useEffect, useRef, useCallback } from "react";
import API from "services";
import "./style.scss";
import BellaFace from "./Face/bella/BellaFace";
import FaceDemo from "./Face/bella/FaceDemo";
import BootOverlay from "./BootOverlay";
import { BrandInterlude, EngravedLogo, useBrandInterlude } from "./Brand";
import { faceStore } from "./Face/engine/faceStore";
import Subtitle from "components/Subtitle";
import { useLocalIP, useSocket } from "contexts";
import { parseAppMessage } from "./appMessage";

// The Dograh voice assistant, switched off for now. Off, the widget is never
// loaded (no calls, no cost) and her wake word gets WAKE_REPLY instead.
const AI_ASSISTANT = false;

// What Misti says when she hears her wake word while the assistant is off, in
// her own voice from the robot (services/sarvamTtsHandler), with a happy face.
const WAKE_REPLY = process.env.REACT_APP_WAKE_REPLY || "Hi, I'm Misti! How can I help you?";
const WAKE_REPLY_LANGUAGE = process.env.REACT_APP_WAKE_REPLY_LANGUAGE || "en-IN";
// One wake-word phrase can be detected on several frames in a row; one reply is enough.
const WAKE_REPLY_GAP_MS = 2500;

// Set REACT_APP_WAKE_WORD_ENABLED=false to hold one always-open session
// instead. That is billed per minute even in an empty room - see
// README-DOGRAH.md before turning it off.
const WAKE_WORD_ENABLED = process.env.REACT_APP_WAKE_WORD_ENABLED !== "false";

// Hard backstop. Covers a dropped turn or a pipeline error, which would
// otherwise leave the mic open indefinitely.
const MIC_OPEN_MS = Number(process.env.REACT_APP_MIC_OPEN_SECONDS || 45) * 1000;

// Only used when Misti has asked the guest something. She keeps listening for
// this long so they can just answer - no wake word needed mid-question. If she
// merely stated something, the exchange is over and the mic closes immediately.
//
// Too short and you cut people off while they think; too long and the robot
// listens to an empty room. Around ten seconds suits someone at a counter
// deciding what to order.
const FOLLOWUP_MS = Number(process.env.REACT_APP_FOLLOWUP_SECONDS || 10) * 1000;

// How long a caption stays on screen after the last thing was said. Without
// this the final line of a conversation sits on Misti's face indefinitely,
// which looks broken to the next guest who walks up.
const SUBTITLE_MS = Number(process.env.REACT_APP_SUBTITLE_SECONDS || 6) * 1000;

// ?face=demo pins the face to any state or emotion for review and tuning.
const FACE_DEMO = new URLSearchParams(window.location.search).get("face") === "demo";

// Everything the face shows comes from these events - see Face/engine. The face
// never reads React state, so none of this causes a re-render.
const face = (type, extra) => faceStore.dispatch({ type, ...extra });

// The emotion she wears for a line from the phone app: the one the sender chose,
// held for about as long as the line takes to say, or else whatever her words
// suggest.
const showFeeling = (message, emotion) => {
	if (emotion) face("EMOTION", { emotion, ms: Math.max(3000, message.length * 70 + 1000) });
	else face("BOT_TEXT", { text: message });
};

const HomePage = () => {
	const localIP = useLocalIP();
	const { chatSocket } = useSocket();

	const [audioEnabled, setAudioEnabled] = useState(false);
	const [conversationEnabled, setConversationEnabled] = useState(true);
	const [subtitleText, setSubtitleText] = useState("");
	const [callStatus, setCallStatus] = useState("idle");
	// Start-up screen (see BootOverlay): her face shows once the assistant call
	// has come up and the wake word is listening.
	const [callWasUp, setCallWasUp] = useState(false);
	const [wakeReady, setWakeReady] = useState(false);
	const [assistantError, setAssistantError] = useState(false);
	const [booted, setBooted] = useState(false);
	const [overlayGone, setOverlayGone] = useState(false);
	const [bootSlow, setBootSlow] = useState(false);

	const currentAudioRef = useRef(null);
	const widgetReadyRef = useRef(false);
	const micTimerRef = useRef(null);
	const followupTimerRef = useRef(null);
	const wasSpeakingRef = useRef(false);
	const lastBotTextRef = useRef("");
	const micOpenRef = useRef(false);
	const subtitleTimerRef = useRef(null);
	// Lines from the phone app play one after another, never on top of each other.
	const appQueueRef = useRef(Promise.resolve());
	const appPendingRef = useRef(0);
	const appSpeakingRef = useRef(false);
	const micWasOpenRef = useRef(false);

	// Every few minutes while she rests, her face steps aside for the "made by
	// Aeonix" card - never while a line from the app is on its way or her mic is open.
	const brand = useBrandInterlude({
		enabled: booted && !FACE_DEMO,
		isBusy: () => appPendingRef.current > 0 || micOpenRef.current,
	});

	// Shows a caption and clears it a few seconds later. Every new line restarts
	// the countdown, so a flowing conversation keeps its captions and only the
	// last one fades.
	const showSubtitle = useCallback((text) => {
		if (subtitleTimerRef.current) {
			clearTimeout(subtitleTimerRef.current);
			subtitleTimerRef.current = null;
		}
		setSubtitleText(text || "");
		if (!text) return;
		subtitleTimerRef.current = setTimeout(() => setSubtitleText(""), SUBTITLE_MS);
	}, []);

	// Closes the mic and goes back to waiting for the wake word.
	// Defined before the effects that use it - a useCallback is in the temporal
	// dead zone until its line runs, so order matters here.
	const closeMic = useCallback(() => {
		if (micTimerRef.current) {
			clearTimeout(micTimerRef.current);
			micTimerRef.current = null;
		}
		if (followupTimerRef.current) {
			clearTimeout(followupTimerRef.current);
			followupTimerRef.current = null;
		}
		API.setMicEnabled(false);
		micOpenRef.current = false;
		face("MIC", { open: false });
	}, []);

	// The guest is still talking, so the conversation is alive - cancel any
	// pending close.
	const keepConversationAlive = useCallback(() => {
		if (followupTimerRef.current) {
			clearTimeout(followupTimerRef.current);
			followupTimerRef.current = null;
		}
	}, []);

	// Misti has finished a reply. Whether the guest needs the wake word again
	// depends on what she just said.
	//
	// If she asked a question, she is waiting for an answer - it would be absurd
	// to make someone say "Alexa" to reply to "what would you like to drink?".
	// So the mic stays open for FOLLOWUP_MS.
	//
	// If she only stated something - gave directions, confirmed an order - the
	// exchange is finished. The mic closes and she waits to be called again.
	//
	// The test is the question mark, which Bengali and Hindi both use, so this
	// works across all three languages. A short grace period is still allowed
	// after a statement, in case the guest immediately follows up.
	const finishTurn = useCallback(() => {
		if (followupTimerRef.current) clearTimeout(followupTimerRef.current);

		const asked = /[?？]\s*$/.test((lastBotTextRef.current || "").trim());
		const wait = asked ? FOLLOWUP_MS : 2500;

		console.log(asked ? `[mic] she asked something - listening ${wait / 1000}s for an answer` : "[mic] statement, not a question - closing shortly");

		followupTimerRef.current = setTimeout(() => {
			console.log("[mic] no reply - wake word needed again");
			closeMic();
		}, wait);
	}, [closeMic]);

	// ---------------------------------------------------------------------
	// Teleoperation: speak text pushed from the mobile app.
	// Separate from the Dograh conversation - this is a person driving Misti.
	// ---------------------------------------------------------------------
	const handleOnlySpeech = useCallback(async (message, language, emotion = null) => {
		if (!message || !message.trim()) return false;

		let url = null;
		let played = false;
		try {
			// Made and kept by the robot (services/sarvamTtsHandler); a saved
			// message is already there, so this is near-instant.
			const clip = await API.synthesizeSpeech(message, language);
			// A person is driving Misti here, but she should still wear the
			// emotion of what she is saying, same as in a conversation.
			showFeeling(message, emotion);
			face("BOT_SPEAKING", { speaking: true });
			showSubtitle(message);

			url = URL.createObjectURL(clip);
			const audio = new Audio(url);
			currentAudioRef.current = audio;

			// Handlers go on before play(): a clip that fails to decode, or ends
			// before play() settles, must still finish the line - otherwise the
			// queue stalls and her mic never reopens.
			await new Promise((resolve, reject) => {
				audio.onended = resolve;
				audio.onerror = () => reject(new Error("audio could not be played"));
				audio.play().catch(reject);
			});
			played = true;
			return true;
		} catch (err) {
			// If she cannot say it (no speech key, network down), she at least shows
			// it, so whoever sent it can see it arrived.
			console.error("Teleop speech failed:", err);
			showFeeling(message, emotion);
			showSubtitle(message);
			return false;
		} finally {
			face("BOT_SPEAKING", { speaking: false });
			if (played) showSubtitle("");
			currentAudioRef.current = null;
			if (url) URL.revokeObjectURL(url);
		}
	}, [showSubtitle]);

	// While she says a line from the app, she stops listening: the call's mic and
	// the wake word both pause, so she neither hears herself (a line with "Misti"
	// in it would wake her) nor answers her own words. Her conversational voice is
	// muted too, so the two never talk over each other.
	const pauseListening = useCallback(() => {
		micWasOpenRef.current = micOpenRef.current;
		appSpeakingRef.current = true;
		closeMic();
		API.pauseWakeWord();
		API.setAgentAudioMuted(true);
		faceStore.setAmplitude(0);
		console.log("[app] speaking a line from the app - listening paused");
	}, [closeMic]);

	// Back to how things were. If a guest was mid-conversation, the mic reopens
	// for a follow-up window so they can carry on without the wake word.
	const resumeListening = useCallback(() => {
		appSpeakingRef.current = false;
		API.setAgentAudioMuted(false);
		if (!WAKE_WORD_ENABLED) {
			API.setMicEnabled(true);
			face("MIC", { open: true });
		} else {
			API.resumeWakeWord(); // queued behind the pause - see wakeWordHandler
			if (micWasOpenRef.current) {
				API.setMicEnabled(true);
				micOpenRef.current = true;
				face("MIC", { open: true });
				micTimerRef.current = setTimeout(closeMic, MIC_OPEN_MS);
				followupTimerRef.current = setTimeout(closeMic, FOLLOWUP_MS);
			}
		}
		console.log("[app] line finished - listening again");
	}, [closeMic]);

	// Queues a line. Listening pauses as soon as the first one arrives - before
	// its speech is even generated - and resumes after the last one ends.
	const sayFromApp = useCallback(
		(text, language, emotion) => {
			if (appPendingRef.current++ === 0) pauseListening();
			appQueueRef.current = appQueueRef.current
				.then(() => handleOnlySpeech(text, language, emotion))
				.catch(() => {})
				.then(() => {
					if (--appPendingRef.current === 0) resumeListening();
				});
		},
		[handleOnlySpeech, pauseListening, resumeListening]
	);

	// ---------------------------------------------------------------------
	// Socket messages from the mobile app (see ./appMessage)
	// ---------------------------------------------------------------------
	useEffect(() => {
		if (!chatSocket) return undefined;
		const handleSocketMessage = (msg) => {
			const m = parseAppMessage(msg);
			if (!m) return;
			if ("ai" in m) setConversationEnabled(m.ai);
			else if (m.say) sayFromApp(m.say, m.language, m.emotion);
			// Just a face, no words - it does not interrupt anything she is saying.
			else if (m.emotion) face("EMOTION", { emotion: m.emotion });
		};
		chatSocket.on("message", handleSocketMessage);
		return () => chatSocket.off("message", handleSocketMessage);
	}, [chatSocket, sayFromApp]);

	// ---------------------------------------------------------------------
	// Dograh: load the widget and register lifecycle callbacks once.
	// start() itself needs a user gesture, so it waits for the Start button.
	// ---------------------------------------------------------------------
	useEffect(() => {
		if (!AI_ASSISTANT) return undefined; // assistant off: no widget, no calls
		let cancelled = false;

		API.loadDograhWidget({ robot: "misti", local_ip: localIP || "" })
			.then((widget) => {
				if (cancelled) return;
				widgetReadyRef.current = true;

				widget.onStatusChange((status) => {
					setCallStatus(status);
					face("CALL_STATUS", { status });
					if (status === "connected") {
						setCallWasUp(true);
						setAssistantError(false);
					} else if (status === "failed") {
						setAssistantError(true);
					}
				});

				widget.onCallConnected(() => {
					// Lip-sync. The detector measures her actual output every frame -
					// how loud, and from the spectrum which mouth shape the sound
					// needs - and it goes straight to the face, never through React.
					// Whether she IS speaking comes from the event tap below - the
					// pipeline's own start/stop events are exact, inference is not.
					// While she says a line from the app her call voice is muted, so its
					// levels would move her mouth to words nobody hears.
					API.startSpeakingDetector((speaking, level, shape) => {
						if (!appSpeakingRef.current) faceStore.setAmplitude(speaking ? level : 0, shape);
					});

					// Exact events from the pipeline, rather than inference. These
					// also stop the widget logging them as "Unknown message type".
					API.startEventTap({
						onBotSpeaking: (speaking) => {
							// A line from the app owns her face until it ends.
							if (!appSpeakingRef.current) face("BOT_SPEAKING", { speaking });

							if (speaking) {
								// She has started answering - cancel any pending close.
								keepConversationAlive();
							} else if (wasSpeakingRef.current && micOpenRef.current) {
								// Her turn just ended. Whether the guest can reply
								// straight away depends on whether she asked anything.
								//
								// Guarded on micOpenRef because rtf-bot-stopped-speaking
								// fires once per utterance chunk, not once per turn, and
								// would otherwise keep re-arming the timer after the mic
								// had already closed.
								if (WAKE_WORD_ENABLED) finishTurn();
							}
							wasSpeakingRef.current = speaking;
						},
						onUserSpeaking: () => {
							// Mid-sentence - she is hearing them.
							face("USER_SPEAKING");
							keepConversationAlive();
						},
						onUserTranscript: (text) => {
							if (text) showSubtitle(text);
							// They have finished; the answer is on its way.
							face("USER_FINAL");
							// The guest is still engaged, so the conversation carries on
							// without another wake word.
							keepConversationAlive();
						},
						onToolCall: (started) => {
							// Looking something up - the menu, an order.
							face("TOOL", { started });
							if (started) keepConversationAlive();
						},
						onBotText: (text) => {
							if (text && !appSpeakingRef.current) {
								face("BOT_TEXT", { text });
								showSubtitle(text);
								// Remembered so finishTurn can tell a question from a
								// statement once she stops speaking.
								lastBotTextRef.current = text;
							}
						},
						onLatency: ({ type, ms, model }) => {
							// Worth watching: anything much over a second here is what a
							// guest experiences as Misti being slow to answer.
							console.log(`[latency] ${type.replace("rtf-", "")}: ${ms}ms${model ? ` (${model})` : ""}`);
						},
						debug: false,
					});

					// The call is up but Misti is not listening yet. The mic stays
					// closed until the wake word opens it, so the connection is
					// already warm when someone speaks - no setup delay.
					if (WAKE_WORD_ENABLED) {
						API.setMicEnabled(false);
						face("MIC", { open: false });
					}
				});

				widget.onCallEnd(() => {
					API.stopSpeakingDetector();
					API.stopEventTap();
					wasSpeakingRef.current = false;
					faceStore.setAmplitude(0);
					face("BOT_SPEAKING", { speaking: false });
					face("MIC", { open: false });
				});

				widget.onError((err) => {
					console.error("Dograh widget error:", err);
					face("ERROR");
				});
			})
			.catch((err) => {
				console.error("Could not load Dograh widget:", err);
				setAssistantError(true);
			});

		return () => {
			cancelled = true;
			API.stopSpeakingDetector();
			API.stopEventTap();
			// The wake word is not stopped here: its own effect owns it, and a
			// second stop from this cleanup would race that effect's restart.
			if (micTimerRef.current) clearTimeout(micTimerRef.current);
			if (followupTimerRef.current) clearTimeout(followupTimerRef.current);
			if (subtitleTimerRef.current) clearTimeout(subtitleTimerRef.current);
		};
		// Deliberately not depending on localIP: it flips from "Fetching..." to
		// the real address shortly after mount, and re-running this would tear
		// down the wake-word detector mid-listen.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Opens a Dograh session. Safe to call from a wake-word callback: the mic
	// permission was already granted by the Start Misti click, and browsers
	// remember that grant per origin, so no second prompt appears.
	const startConversation = useCallback(() => {
		if (!AI_ASSISTANT || !window.DograhWidget || !conversationEnabled) return;
		const { connectionStatus } = window.DograhWidget.getState();
		if (connectionStatus === "connected" || connectionStatus === "connecting") return;
		window.DograhWidget.start();
	}, [conversationEnabled]);

	// Stop the conversation when the mobile app turns AI mode off.
	useEffect(() => {
		if (!widgetReadyRef.current || !audioEnabled) return;
		if (!conversationEnabled && callStatus === "connected" && window.DograhWidget) {
			window.DograhWidget.end();
		}
	}, [conversationEnabled, audioEnabled, callStatus]);

	// Dograh caps a call at 20 minutes (MAX_CALL_DURATION_SECONDS), so a
	// permanently connected robot is not possible - the session will drop on its
	// own. Bring it straight back, otherwise Misti goes deaf until someone
	// reloads the page.
	useEffect(() => {
		if (!AI_ASSISTANT || !audioEnabled || !conversationEnabled) return;
		if (callStatus !== "idle" && callStatus !== "failed") return;

		const delay = callStatus === "failed" ? 5000 : 1000;
		const t = setTimeout(() => {
			console.log(`[call] reconnecting after status "${callStatus}"`);
			startConversation();
		}, delay);
		return () => clearTimeout(t);
	}, [callStatus, audioEnabled, conversationEnabled, startConversation]);

	// Opens the mic for one exchange. It normally closes again as soon as Misti
	// finishes her reply (see the speaking detector above). The timer is only a
	// backstop for the case where she never answers at all - a dropped turn or a
	// pipeline error would otherwise leave the mic open indefinitely.
	const openMicWindow = useCallback(() => {
		API.setMicEnabled(true);
		micOpenRef.current = true;
		face("WAKE");

		if (micTimerRef.current) clearTimeout(micTimerRef.current);
		micTimerRef.current = setTimeout(() => {
			console.log("[mic] backstop timeout - no reply came, closing");
			closeMic();
		}, MIC_OPEN_MS);
	}, [closeMic]);

	// Assistant off: her wake word gets the fixed reply. The quick "she heard you"
	// look first, then the reply - which pauses listening while she speaks, so
	// she cannot wake herself. Repeat detections of the same phrase are ignored.
	const lastWakeReplyRef = useRef(0);
	const replyToWake = useCallback(() => {
		const now = Date.now();
		if (appPendingRef.current > 0 || now - lastWakeReplyRef.current < WAKE_REPLY_GAP_MS) return;
		lastWakeReplyRef.current = now;
		console.log("[wake] fixed reply (assistant off)");
		face("WAKE");
		sayFromApp(WAKE_REPLY, WAKE_REPLY_LANGUAGE, "happy");
	}, [sayFromApp]);

	// The wake word listens for as long as audio is on. It lives in an effect,
	// not in the Start click, so that anything which re-runs effects - a hot
	// reload while the app is being worked on, React remounting the page -
	// restarts it. Started from the click alone, the first such re-run stopped
	// it for good: her mic could never open again, and every guest who spoke to
	// her was talking to a closed mic.
	const wakeStopRef = useRef(Promise.resolve());
	useEffect(() => {
		if (!audioEnabled || !WAKE_WORD_ENABLED) return undefined;
		let cancelled = false;
		(async () => {
			// Stopping releases the mic asynchronously; starting before that has
			// finished would find the old detector still "running" and skip.
			await wakeStopRef.current;
			if (cancelled) return;
			const ok = await API.startWakeWord({ onWake: () => (AI_ASSISTANT ? openMicWindow() : replyToWake()) });
			// Started or not, there is nothing more to wait for: a detector that
			// failed leaves the mic open instead, so she can still hear.
			if (!cancelled) setWakeReady(true);
			if (!ok && !cancelled) {
				console.warn("[wake] detector failed to start - leaving the mic open");
				API.setMicEnabled(true);
				face("MIC", { open: true });
			}
		})();
		return () => {
			cancelled = true;
			wakeStopRef.current = API.stopWakeWord();
		};
	}, [audioEnabled, openMicWindow, replyToWake]);

	// Her face shows once she can hold a conversation: the call has come up (or
	// the app has switched the assistant off) and the wake word is listening.
	// Only at start-up - later reconnects are the face's business, not a reason
	// to hide it again.
	useEffect(() => {
		if (booted || !audioEnabled) return;
		const callReady = !AI_ASSISTANT || callWasUp || !conversationEnabled;
		const earsReady = wakeReady || !WAKE_WORD_ENABLED;
		if (callReady && earsReady) setBooted(true);
	}, [booted, audioEnabled, callWasUp, conversationEnabled, wakeReady]);

	// The blur fades out (see .boot-overlay.is-leaving), then goes for good.
	useEffect(() => {
		if (!booted) return undefined;
		const t = setTimeout(() => setOverlayGone(true), 900);
		return () => clearTimeout(t);
	}, [booted]);

	// If waking takes unusually long - no network, the assistant down - staff can
	// still bring her face up rather than stare at a blurred screen.
	useEffect(() => {
		if (!audioEnabled || booted) return undefined;
		const t = setTimeout(() => setBootSlow(true), 45000);
		return () => clearTimeout(t);
	}, [audioEnabled, booted]);

	// The browser requires a user gesture before granting mic access, so this
	// click is unavoidable - one tap at power-on. After that Misti is autonomous.
	const handleEnableAudio = async () => {
		setAudioEnabled(true);
		face("START");

		// Connect straight away and keep the call up. Reconnection costs a
		// second or two of WebRTC setup, which a guest would hear as a pause.
		startConversation();

		if (!WAKE_WORD_ENABLED) {
			// No wake word means an always-open mic, so she is always listening.
			face("MIC", { open: true });
		}
		// Otherwise the wake word controls the microphone, not the call; the
		// effect above starts it now that audio is enabled.
	};

	// A tap on her face gets the reaction BellaBot gives a head pat. The screen is
	// the one thing on the robot guests reliably touch, so it may as well be
	// rewarded rather than ignored.
	const handlePoke = () => {
		// A tap during the brand card brings her face straight back.
		if (brand.phase === "on") {
			brand.leave();
			return;
		}
		if (audioEnabled) face("POKE");
	};

	return (
		<div className="app-container">
			{/* The play button collects the one user gesture browsers demand before
			    granting the mic; the same blurred screen then says she is waking up
			    until the assistant and the wake word are both ready. */}
			{!FACE_DEMO && !overlayGone && (
				<BootOverlay
					phase={!audioEnabled ? "tap" : booted ? "leaving" : "waking"}
					step={assistantError ? "error" : !AI_ASSISTANT || callWasUp || !conversationEnabled ? "ears" : "voice"}
					slow={bootSlow}
					onStart={handleEnableAudio}
					onSkip={() => setBooted(true)}
				/>
			)}

			<div className={`face-stage${brand.phase === "on" ? " is-away" : ""}`} onPointerDown={handlePoke}>
				<BellaFace />
			</div>
			<EngravedLogo />
			<BrandInterlude phase={brand.phase} />
			{FACE_DEMO && <FaceDemo />}

			{/* Whether she is listening is shown by the face's edge aura (cyan while
			    the mic is open), which replaced the rainbow bar that used to sit here. */}
			<Subtitle text={subtitleText} />
		</div>
	);
};

export default HomePage;
